import { faPlay } from '@fortawesome/free-solid-svg-icons';

import { APP_URLS } from '../routes/paths';

export const APP_TEXT = {
  app: {
    title: 'Digital Skills',
  },
  pages: {
    landingPage: {
      metaData: {
        title: 'Welcome - Digital Skills',
      },
      title: 'Welcome to Digital Skills',
      subtitle: 'Your trusted companion for digital growth',
      body1:
        'Digital Skills brings together learning resources from across the University of Exeter and beyond, designed to build your digital confidence and develop fundamental skills to work smarter and achieve your goals.',
      body2:
        'Start with our  __self-assessment quiz__  to gauge your current confidence levels, reveal areas for development and get personalised recommendations tailored to you and your role.',
      body3:
        'Digital Skills and the initiative behind it aligns directly to the __University’s Digital Strategy__ and __People Strategy 2030__ and supports our mission to empowering every colleague to thrive in ever-evolving digital world.',

      ctaDiagnostic: 'Begin diagnostic tool',
      ctaLibrary: 'Digital skills library',
      cards: [
        {
          id: 0,
          image: 'faClock',
          title: 'Check your confidence with the Self-Assessment Quiz',
          description:
            'Understand your digital strenths, identify areas for development, and discover learning recommendations based on your department, role and interests.',
          buttonTitle: 'Take the quiz',
          url: APP_URLS.introPage,
          imageName: 'being-creative.svg',
          imageAlt: 'being creative',
        },
        {
          id: 1,
          image: 'faSliders',
          title: 'Explore learning resources in the Digital Skills Library',
          description:
            'Browse a curated collection of high-quality resources to support your day-to-day work - from courses on essential tools to more advanced skills for future-ready working.',
          buttonTitle: 'Browse the Digital Skills Library',
          url: APP_URLS.library,
          imageName: 'searching-streamline.svg',
          imageAlt: 'searching streamline',
        },
      ],
      explaination: {
        metaData: {
          title: 'Get the most out of Digital Skills',
        },
        title: 'Get the most out of Digital Skills',
        cards: [
          {
            id: 0,
            imageName: 'asset_sliders.svg',
            imageAlt: 'Graphic representing a quiz',
            title: 'Complete the Self-Assessment Quiz',
            description:
              'You’ll be asked questions around your confidence and motivation in fifteen categories of digital skills. There are no wrong answers!',
          },
          {
            id: 1,
            imageName: 'asset_write.svg',
            imageAlt: 'Graphic representing pages of information',
            title: 'Access your digital Skill Stats',
            description:
              'When you’ve finished the quiz, you’ll gain access to your Skill Stats. Visually see your digital strengths and areas for development.',
          },
          {
            id: 2,
            imageName: 'asset_book.svg',
            imageAlt: 'Graphic representing a book',
            title: 'Explore personalised recommendations',
            description:
              'Your answers are used to generate a private collection of recommended learning tailored to your interests and goals, to help improve your digital confidence and capability.',
          },
        ],
        actionButtons: {
          buttonOne: {
            startIcon: null,
            title: 'Start the quiz',
            url: APP_URLS.introPage,
          },
          buttonTwo: {
            startIcon: faPlay,
            title: 'Watch demo',
            url: APP_URLS.library,
          },
        },
      },
      demoVideo: {
        music: { key: 'music', value: 'Bensound' },
        artist: { key: 'artist', value: 'Benjamin Tissot' },
        licenseCode: { key: 'licenseCode', value: 'XRGNHWVXEENWKARW' },
        label: 'Video modal scrollable content',
      },
    },
    introPage: {
      metaData: {
        title: 'Introduction - Self-Assessment Quiz',
      },
      title: 'The Self-Assessment Quiz',
      body1:
        "This quiz will help you better understand which digital skills you're confident in and where you could develop further.",
      body2:
        'There are 15 digital skills areas to consider, with two or three simple questions asked in each.',
      footer:
        '*We suggest that learning recommendations are shared with your line manager to best support your development and prompt discussion during your PDR. You can do this from the Skills Stats page visible to you once you’ve completed the Self-Assessment Quiz.',
      continue: 'Continue',
      cards: [
        {
          id: 0,
          icon: 'asset_clock.svg',
          imageAlt: 'Graphic representing a clock',
          title: 'Time',
          description:
            'The quiz can take up to 30 minutes. You can save, exit and return at any time.',
        },
        {
          id: 1,
          icon: 'asset_quiz.svg',
          imageAlt: 'Graphic representing a quiz',
          title: 'Quiz',
          description: "It's a quiz, not a test, so there are no wrong answers.",
        },
        {
          id: 2,
          icon: 'asset_privacy.svg',
          imageAlt: 'Graphic representing information privacy',
          title: 'Privacy',
          description:
            'Your answers always stay private. Learning recommendations can be shared with your line manager if you choose to.*',
        },
      ],
    },
    quiz: {
      metaData: {
        title: 'Question - Self-Assessment Quiz',
      },
      profile: 'Profile',
      quizUnavailable: 'Quiz unavailable',
      continueError: 'Please answer the questions above to continue.',
      noSkillCategories:
        'Currently no skill categories have been created for your user type, please contact an admin to request them.',
      somethingWentWrong: 'Something went wrong, please contact an admin to report this issue.',
      slider: {
        notAtAll: 'Not at all',
        notVery: 'Not very',
        unsure: 'Unsure',
        fairly: 'Fairly',
        very: 'Very',
      },
      profileSelection: {
        metaData: {
          title: 'Profile Selection - Self-Assessment Quiz',
        },
        title: 'Profile selection',
        body1:
          'Before starting the self-assessment please choose a profile that best matches your role and responsibilities at the university. This will personalise the self-assessment quiz to your particular area of work and thereby better support your learning journey.',
        body2:
          'Please note: All profiles are part of the [Jisc Building digital capabilities framework](https://repository.jisc.ac.uk/8846/1/2022_Jisc_BDC_Individual_Framework.pdf) and are not a measure of competency.',
        error: 'There was an error loading the profiles, please try again later.',
        missingProfileError: 'Select your profile before starting the quiz',
        empty:
          'There are no profiles available for your user type, please contact an admin to request them.',
        selectPlaceholder: 'Choose profile',
        select: 'Select',
        selected: 'Selected',
        startQuiz: 'Start quiz',
        exit: 'Exit',
      },
      intro: {
        body: 'Let’s see what skills you need to work on',
        emoji: '😊',
        button: 'Start',
      },
      question: {
        skillDescription: {
          showExamples: 'Show examples',
          hideExamples: 'Hide examples',
        },
        skillQuestion: {
          instructions: 'Follow the steps below to complete this section, then continue.',
          confident: 'Confident?',
          howConfident: 'How confident do you feel in',
          motivated: 'Motivated?',
          howMotivated: 'How motivated are you to learn more about topics related to',
          suggestedResources: 'Suggested topics',
          suggestedResourcesBody:
            'Your department has identified these digital skills as being relevant to your role.​',
          suggestedResourcesAction:
            'Please tick any tools or topics you would like further training on:',
          noneApplicable: 'None applicable',
          confidentLevel: 'Confidence Level',
          interestLevel: 'Interest Level',
        },
      },
      saveExit: {
        buttonTitle: 'Save & Exit',
        buttonModalTitle: 'Save & Exit',
        buttonModalReturnTitle: 'Exit without saving',
        exit: 'Are you off?',
        warning:
          'You’ve completed {0}%. If you save and exit you can come back at any point and continue where you left.',
        // description: 'Exiting without saving means your progress will not be saved.',
      },
      restart: {
        buttonTitle: 'Restart',
        buttonModalTitle: 'Restart quiz',
        exit: 'Need to restart?',
        warning:
          'Restarting the quiz will remove all your answers and take you back to the introduction page.',
      },
      welcomeBack: {
        title: 'Welcome back',
        warning: 'You’ve completed {0}%. Let’s carry on from where you left.',
        buttonModalReturnTitle: 'Resume the self-assessment quiz',
        buttonModalActionTitle: 'Search digital skills library',
      },
      help: {
        buttonTitle: 'Help',
      },
      quizFinished: {
        title: 'You’re all done!',
        descriptionOne: 'Thank you for completing the Digital Skills Self-Assessment Quiz.',
        descriptionTwo:
          'Click continue to submit your answers and generate your personal Skill Stats.',
        continue: 'Continue',
        returnToQuiz: 'Return to quiz',
      },
    },
    library: {
      metaData: {
        title: 'Digital Skills Library - Digital Skills',
      },
      title: 'Digital Skills Library',
      subtitle: 'Digital skills learning resources to support your ongoing development.',
      description: 'Digital skills learning resources to support your ongoing development.',
      descriptionOne:
        'Contains internally produced webpages and courses, as well as curated external courses and information.',
      descriptionTwo:
        'Please be aware that resources for the “Digital Teaching” and “Digital Development” categories are not currently available. We are working to add these soon and in the meantime please continue to use your current routes for support.',
      speechBubble: {
        title: 'The Jisc Building digital capabilities framework',
        body: 'All our library resources are categorised into one of 15 digital capabilities, as defined by the [Jisc Building digital capabilities framework](https://repository.jisc.ac.uk/8846/1/2022_Jisc_BDC_Individual_Framework.pdf). These capabilities have been grouped into 6 elements as detailed below:',
      },
      searchPlaceholder: 'Search the library...',
      filter: 'Filter',
      search: 'Search',
      clearAll: 'Clear all',
      viewRecommendedResources: 'View recommended resources',
      leaveFeedback: 'Leave feedback',
      filters: {
        digitalSkillsCategory: {
          title: 'Digital skills category',
          description:
            'You can filter by the 15 digital skills areas, as defined in the Jisc digital capabilities framework.',
          label: 'Filter by skills category',
        },
        resourceType: {
          title: 'Resource type',
          description:
            'Choose from informative web pages to self-led training courses, based on your level of interest and need.',
          label: 'Filter by resource type',
        },
        resourceLocation: {
          title: 'Resource location',
          description:
            'This indicates whether a resource has been produced by peers across the University of Exeter or links to an external resource.',
          label: 'Filter by resource location',
        },
        searchByKeywords: {
          title: 'Search by keywords',
          description:
            'You can also use the search bar to find specific resources based on keywords.',
          label: 'Search by keywords',
        },
      },
      categoryOptions: {
        creativeProduction: {
          title: 'Creative Production',
          description: 'Digital creation, digital research and problem-solving, digital innovation',
        },
        criticalUse: {
          title: 'Critical Use',
          description: 'Information literacy, data literacy, media literacy',
        },
        development: {
          title: 'Development',
          description: 'Digital learning, digital teaching/development',
        },
        selfActualising: {
          title: 'Self-Actualising',
          description: 'Digital identity management, digital wellbeing',
        },
        participation: {
          title: 'Participation',
          description: 'Digital communication, digital collaboration, digital participation',
        },
        functionalSkills: {
          title: 'Functional Skills',
          description: 'Digital proficiency, digital productivity',
        },
      },
      feedbackForm: 'https://forms.office.com/e/ZAFsgfiAaj',
      footer: {
        title: 'Not sure where to get started?',
        buttonText: 'Take the self-assessment quiz',
      },
    },
    yourLearning: {
      metadata: {
        title: 'Your Learning - Digital Skills',
      },
      title: 'Your Learning',
    },
    resourceManager: {
      metaData: {
        title: 'Resource Manager - Digital Skills',
      },
      title: 'Resource Manager',
      description: 'Add, edit and manage the Digital Skills resources',
      addResource: 'Add resource',
      table: {
        filters: {
          searchPlaceholder: 'Search...',
          digitalSkillsCategory: {
            title: 'Digital skills category',
            description: 'Filter by digital skills category',
            label: 'Filter by skills category',
          },
          resourceType: {
            title: 'Type',
            description: 'Filter by resource type',
            label: 'Filter by resource type',
          },
          resourceLocation: {
            title: 'Resource location',
            description: 'Filter by resource location',
            label: 'Filter by resource location',
          },
          status: {
            title: 'Status',
            description: 'Filter by status',
            label: 'Filter by status',
          },
        },
        clearAll: 'Clear all',
        columnTitles: {
          title: 'Resource title',
          digitalSkillsCategory: 'Skills category',
          skillLevel: 'Skill level',
          topic: 'Topic',
          type: 'Type',
          modified: 'Modified',
          status: 'Status',
          actions: 'Actions',
        },
      },
    },
    bookmarkedResources: {
      metadata: {
        title: 'Saved Resources - Digital Skills',
      },
      noBookmarks: {
        subtitle: "You haven't saved any resources yet",
        filteredSubtitle: 'No resources found for the selected filters.',
        buttonText: 'Browse resources',
        imageAltText: 'No bookmarks found',
        takeQuizSection: {
          title: 'Not sure where to get started?',
          buttonText: 'Take the Self-Assessment Quiz',
        },
      },
      title: 'Saved Resources',
      subtitle: "You've saved {bookmarkedResources}. Let's get learning!",
      filters: {
        digitalSkillsCategory: {
          title: 'Digital skills category',
          description:
            'You can filter by the 15 digital skills areas, as defined in the Jisc digital capabilities framework.',
          label: 'Filter by skills category',
        },
        resourceType: {
          title: 'Resource type',
          description:
            'Choose from informative web pages to self-led training courses, based on your level of interest and need.',
          label: 'Filter by resource type',
        },
        resourceLocation: {
          title: 'Resource location',
          description:
            'This indicates whether a resource has been produced by peers across the University of Exeter or links to an external resource.',
          label: 'Filter by resource location',
        },
        searchByKeywords: {
          title: 'Search by keywords',
          description:
            'You can also use the search bar to find specific resources based on keywords.',
          label: 'Search by keywords',
        },
      },
      toast: {
        bookmarkCreatedMessage: '{resourceTitle} has been added to your saved resources',
        bookmarkDeletedMessage: '{resourceName has been removed from your saved resources',
      },
      searchPlaceholder: 'Search your saved resources...',
      filter: 'Filter',
      search: 'Search',
      clearAll: 'Clear all',
      deleteAllBookmarks: 'Remove all',
    },
    skillStats: {
      pageTitle: 'Skill Stats - Digital Skills',
      title: 'Skill stats',
      privacyLabel:
        'Your individual confidence scores are private to you. Click [here](https://www.exeter.ac.uk/departments/digital/productsandservices/digitalskillswebapptermsofuse/) for more info on data sharing.',
      subTitleParagraph1:
        'Below is a summary of your digital skills confidence levels categorised by the [Jisc capabilities framework.](https://repository.jisc.ac.uk/8846/1/2022_Jisc_BDC_Individual_Framework.pdf) This gives you an overview of your strengths and potential development areas.',
      subTitleParagraph2:
        'Your Skill Stats are calculated using your self-assessment quiz inputs from',
      loadingAriaLabel: 'Loading your skill stats',
      mainElAriaLabel: 'Your skill stats page',
      card: {
        category: 'Digital Skills category',
        confidence: 'Confidence level',
        overallConfidence: 'Overall confidence level',
        trendUp: 'You’ve improved since your last quiz ',
        maxConfidence: 'You’ve mastered this category!',
        buttonLabel: 'View recommended resources in this area',
        trendUpIcon: '/assets/trend_up.svg',
        trendUpIconAlt: 'Arrow pointing upwards indicating an improvement in confidence',
        trendMaxIcon: '/assets/trend_max.svg',
        trendMaxIconAlt: 'Diamond icon indicating maximum confidence achieved',
      },
      footer: {
        imageAlt: 'Graphic representing a lightbulb moment',
        imageIcon: '/assets/being-creative.svg',
        title: 'Ready to take the next step in your upskilling journey?',
        buttonText: 'Explore your recommended resources',
        buttonAriaLabel: 'Explore your recommended resources button',
      },
    },
    learningProgress: {
      title: 'Progress',
      subtitle: 'View the resources you’ve started and those you’ve completed',
      metadata: {
        title: 'Your Learning Progress - Digital Skills',
      },
      noneInProgress: {
        subtitle: "You haven't started any resources yet",
        buttonText: 'Browse resources',
        imageAltText: 'No resources currently in progress',
        takeQuizSection: {
          title: 'Not sure where to get started?',
          buttonText: 'Take the Self-Assessment Quiz',
        },
      },
      accessibility: {
        filterByStartedBtn: 'Button to show only resources that you have started',
        filterByCompletedBtn: 'Button to show only resources that you have completed',
      },
      searchPlaceholder: 'Search...',
    },
    recommendation: {
      pageTitle: 'Recommended for you - Digital Skills',
      title: 'Recommended for you',
      subTitle: 'Your recommendations are calculated using your quiz inputs from',
      loadingAriaLabel: 'Loading your recommendations',
      noResults: 'No recommendations found for the selected filters.',
      card: {
        overallConfidence: 'Overall confidence level',
      },
      filters: {
        topicFilterTitle: 'Your topics: ',
        topicFilterShowMore: '+ Show more',
        topicFilterShowLess: '- Show less',
        searchPlaceholder: 'Search your recommendations...',
      },
      sharingYourRecommendations: {
        title: 'Sharing your recommendations with your manager',
        body: 'You can choose to share your learning recommendations with your manager by sending them an auto-filled email via the button on the right. It is recommended to do this at least once a year in advance of your Performance Development Review, to help support your conversation. You will be able to edit the email prior to sending it.',
        shareWithManager: 'Share with manager',
        shareWithManagerToggleLabel: 'Share with manager on/off',
        icon: { url: 'file_eye.svg', alt: 'Graphic representing access control for data' },
        composeEmail: {
          buttonLabel: 'Compose email',
          subject: 'Digital Skills Learning Recommendations',
          body: [
            'Hello,',
            '',
            'As my line manager, I wanted to share with you the training resources that have been identified by the University’s Digital Skills Self-Assessment Quiz as being useful for me, as part of my digital upskilling.',
            '',
            'It is recommended that I share these in advance of our Performance Development Review, so that it helps support our conversation.',
            '',
            'Here are the resources that have been recommended to me:',
            '',
            '[RESOURCE_LIST]',
            '',
            'For more information please visit the Digital Skills SharePoint site:',
            `https://universityofexeteruk.sharepoint.com/sites/DigitalSkillsInitiative`,
            '',
            'Regards,',
            '[YOUR_NAME]',
          ],
        },
      },
      searchPlaceholder: 'Search...',
      roleDepartmentMatch: {
        icon: '/assets/target.svg',
        alt: 'Resource is relevant to your role and department',
        description: 'These resources are relevant to your role and department',
        openModalButton: 'What does this mean?',
        modal: {
          title: 'Role specific resources',
          body: 'Your recommended resources have been chosen based on your most recent quiz answers. Some of these resources may also be directly related to digital skills helpful for your role and department. These are labelled with a target icon and are prioritised within each skill category, appearing at the top of their respective category groups.',
          dismissButton: 'Got it',
        },
      },
      footer: {
        title: 'Want to explore more resources?',
        cta: 'Search the full Digital Skills Library',
      },
    },
    page404: {
      metadata: {
        title: '404 - Page not found',
      },
      title: 'Page not found',
      description: {
        para1:
          'The Digital Skills self-assessment quiz and library are only accessible to colleagues who hold Staff IT accounts. We hope to include Associate Staff and Student accounts in the future. For more information please visit the [DSI SharePoint site.](https://universityofexeteruk.sharepoint.com/sites/DigitalSkillsInitiative)',
        para2:
          'If you need further technical assistance please [click here.](https://www.exeter.ac.uk/itservicedesk)',
      },
    },
    page403: {
      metadata: {
        title: '403 - Forbidden',
      },
      title: 'Not currently available',
      description: {
        para1:
          'The Digital Skills self-assessment quiz and library are only accessible to colleagues who hold Staff IT accounts. We hope to include Associate Staff and Student accounts in the future. For more information please visit the [DSI SharePoint site.](https://universityofexeteruk.sharepoint.com/sites/DigitalSkillsInitiative)',
        para2:
          'If you need further technical assistance please [click here.](https://www.exeter.ac.uk/itservicedesk)',
      },
    },
  },
  components: {
    errorBanner: {
      title: "We're experiencing some technical issues",
      descriptionTemplate: {
        withTime: "{message} We'll aim to fix this issue by {time}.",
        withoutTime: "{message} We'll aim to fix this issue as soon as possible.",
      },
    },
    skillsGraph: {
      xLabel: 'Digital Skills',
      yLabel: 'Confidence',
    },
    navBar: {
      home: 'Home',
      library: 'Digital Skills Library',
      yourLearning: 'Your Learning',
      bookmarkedResources: 'Saved resources',
      dashboard: 'Recommended for you',
      learningProgress: 'Progress',
      logOut: 'Log out',
      managersDashboard: 'Managers Dashboard',
      userProgress: 'Progress',
      selfAssessmentQuiz: 'Self-Assessment Quiz',
      skillsQuiz: 'Self-Assessment Quiz',
      retakeSkillsQuiz: 'Retake the self-assessment quiz',
      resourceManager: 'Resource Manager',
      skillStats: 'Skill stats',
      topicManager: 'Topic Manager',
      new: 'NEW',
      statsUnavailableMessage:
        'Take Self-Assessment Quiz to generate your stats and recommendations',
      ariaLabels: {
        navbar: 'Main Page Navigation Bar',
        logo: 'Logo, Navigate to landing page',
        home: 'Navigate to home page',
        skillsQuiz: 'Navigate to self-assessment quiz',
        library: 'Navigate to digital skills library',
        yourLearning: 'Navigate to the your learning page',
        bookmarkedResouces: 'Navigate to the page to view your saved resources',
        userProgress: 'Navigate to the page to view your learning progress',
        learningProgress: 'Navigate to the page to view your learning progress',
        resourceManager: 'Navigate to resource manager',
        skillStats: 'Navigate to the skill stats page',
        topicManager: 'Navigate to topic manager',
        recommendation: 'Navigate to learner recommendations',
        dashboard: 'Navigate to learner dashboard',
        managersDashboard: 'Navigate to managers dashboard',
        logOut: 'Log out',
      },
    },
    resourceTable: {
      view: 'View',
      headers: {
        title: 'Title',
        digitalSkillsCategory: 'Digital skills category',
        resourceType: 'Type',
        resourceLocation: 'Location',
        progress: 'Progress',
        likes: 'Likes',
        hideRecommendation: 'Hide',
        skillLevel: 'Level',
        action: 'Link',
        bookmarkResource: 'Save',
        removeResourceBookmark: 'Remove',
      },
      accessibility: {
        resourceTypeIIcon: 'Resource type information button',
        resourceLocationIIcon: 'Resource location information button',
        progressIcon: 'Progress information button',
        likesIcon: 'Likes information button',
        hideRecommendationIcon: 'Hide learning resource button',
        sortSkillLevelBtn: 'Sort by skill level',
        sortUserProgressStatusBtn: 'Sort by progress',
        bookmarkResource: 'Save resource for learning later',
        removeBookmarkedResource: 'Remove from saved resources',
        loadingResourceSpinner: 'Loading resource information...',
        bookmarkedResource: 'Resource saved for learning later',
        progressInput: 'Progress dropdown status selected: ',
        progressDropdownLabel: 'Select a progress option',
        progressSelect: 'Select progress option',
      },
      tooltips: {
        removeBookmark: 'Remove resource',
        createBookmark: 'Save Resource',
        hideRecommendation: 'Remove Resource',
      },
      roleDepartmentMatch: {
        icon: '/assets/target.svg',
        alt: 'Resource is relevant to your role and department',
      },
      location: {
        internal: 'Internal',
        external: 'External',
        externalWithLogin: 'External*',
      },
      error: 'There was an error loading the resources, please try again later.',
      emptyOptions: 'No available options',
      hideRecommendation: 'Not relevant? Remove resource',
    },
    faq: {
      title: 'FAQs',
      close: 'Close',
    },
    resourceTypeModal: {
      title: 'Resource Type',
      content: [
        {
          title: 'Course',
          body: 'Structured learning that can include lessons, quizzes and reflection tasks.',
        },
        {
          title: 'Document',
          body: 'Guides, reports, and written information on digital skills.',
        },
        {
          title: 'Video',
          body: 'Videos created around a digital skills topic.',
        },
        {
          title: 'Index',
          body: 'A webpage that has a list of links to other relevant resources.',
        },
        {
          title: 'Webpage',
          body: 'Online articles, blog posts, and written content offering insights and tips.',
        },
      ],
      close: 'Close',
    },
    resourceLocationModal: {
      title: 'Resource Location',
      externalResources: {
        title: 'External Resources',
        body: {
          partOne:
            'All of the recommended external resources that you are signposted to are freely available to access. Please note that some resources may offer additional features, such as receiving a certificate, which may require you to create an account with that resource provider. The University does not require you to access these additional features. If you however choose to access them, entirely at your discretion, please do not use your University email address to create an account, as the University will not be responsible for it. To ensure your security, please use a different password for these external accounts than your university password, in accordance with our password policy. This helps protect your university credentials and personal data.',
          partTwo:
            'For the external resources marked with an *, please be aware that these resources will require you to create an account in order to access them. This is entirely optional. If you do decide to proceed, then please ensure to follow the information above regards using a personal account and different password.',
        },
      },
      internalResources: {
        title: 'Internal Resources',
        body: 'Please note that some of the internal resources will contain incidental links to further third party material, which we may not have verified, and which you would therefore be viewing at your own discretion.',
      },
      close: 'Close',
    },
    progressInfoModal: {
      title: 'Progress',
      body: 'Use our progress dropdown to keep record of where you’re up to on any resource. Your can update your progress and change it anytime, it will save this for when you come back and is only visible to you in your account.',
      close: 'Close',
    },
    likesInfoModal: {
      title: 'Likes',
      body: 'Likes can be applied by all users of the Digital Skills platform to show appreciation for the resource. Drop a resource a like if you have enjoyed it and would recommend it to other learners.',
      close: 'Close',
    },
    hideRecommendationModal: {
      title: 'Remove this recommendation?',
      body: 'If you don’t think this resource is relevant to your learning goals you can remove it from your recommendations list. You can always find the resource again by searching the Library.',
      bodyBold: 'Are you sure you want to continue?',
      close: 'Yes, remove this recommendation',
      accessibility: {
        btnAriaDescription: 'Confirm that you want to hide this recommendation',
        dialogAriaLabel: 'Hide recommendation modal',
      },
    },
    externalResourceModal: {
      title: 'External Resources',
      body: 'All of the recommended external resources that you are signposted to are freely available to access. Please note that these resources will require you to create an account with that resource provider. If you choose to access them, entirely at your discretion, you must create a personal account. Please do not use your University email address to create an account, as the University will not be responsible for it. To ensure your security, please use a different password for these external accounts than your university password, in accordance with our password policy. This helps protect your university credentials and personal data.',
      continue: 'I agree to create a personal account',
      back: 'I do not agree',
    },
    evaluateResourceModal: {
      title: 'Was this resource useful?',
      body: 'To help us ensure we’re supplying you and others with informative and engaging resources, please give this resource a thumbs up if you found it useful.',
      close: 'Close',
      continue: 'Yes, it was useful',
      feedback: 'Leave feedback',
      feedbackLink: 'https://forms.office.com/e/ZAFsgfiAaj',
    },
    resourceFormModal: {
      close: 'Close',
      fields: {
        singleSelectDropDownLabel: 'Select ONE from the dropdown menu',
        requiredFields: '(*) Required fields',
        resourceTitle: 'Resource title *',
        resourceUrl: 'URL *',
        resourceSkillCategory: 'Skill category *',
        resourceTopic: 'Topic',
        resourceType: 'Type *',
        resourceLocation: 'Location *',
        resourceSkillLevel: 'Resource skill level *',
        personalLogin: {
          loginRequired: 'Is a personal login required to access the resource? *',
          options: {
            optionYes: 'Yes',
            optionNo: 'No',
          },
        },
      },
    },
    departmentCascadeSelector: {
      profileSelectSublabel: 'You can select multiple profiles',
      profileSelectDisabled: 'Select one or more contract types before selecting a profile',
      departmentSelectSublabel: 'You can select multiple departments',
      departmentSelectDisabled: 'Select contract type and profile in order to select departments',
    },
    addResourceModal: {
      title: 'Add new resource',
      body: 'Please enter the details of the resource you would like to add.',
      addResource: 'Add resource',
      close: 'Close',
    },
    editResourceModal: {
      title: 'Edit resource',
      saveChanges: 'Save changes',
      close: 'Close',
    },
    archiveResourceModal: {
      title: 'Archive resource',
      body: 'Are you sure you want to archive the following resource?',
      warning: 'You can restore the resource if you need to at any point.',
      archiveResource: 'Archive resource',
      close: 'Close',
    },
    publishResourceModal: {
      title: 'Publish resource',
      body: 'Are you sure you want to publish the following resource?',
      warning: 'This will make the resource visible to all users.',
      publishResource: 'Publish resource',
      close: 'Close',
    },
    unpublishResourceModal: {
      title: 'Unpublish resource',
      body: 'Are you sure you want to unpublish the following resource?',
      warning: 'This will hide the resource from all users.',
      unpublishResource: 'Unpublish resource',
      close: 'Close',
    },
    deleteResourceModal: {
      title: 'Delete resource',
      body: 'Are you sure you want to permanently delete the following resource?',
      warning: 'This action cannot be undone.',
      deleteResource: 'Delete resource',
      close: 'Close',
    },
    restoreResourceModal: {
      title: 'Restore resource',
      body: 'Are you sure you want to restore the following resource?',
      warning: 'This will move the resource back to draft status.',
      restoreResource: 'Restore resource',
      close: 'Close',
    },
    shareToManagerModal: {
      title: 'Share your learning recommendations with your manager',
      formFields: {
        to: 'To:',
        cc: 'Cc:',
        subjectLine: 'Subject line',
        subjectLinePlaceholder: 'Digital Skills Learning Recommendation ([YOUR_NAME])',
        toPlaceholder: 'Enter email address',
        ccPlaceholder: 'Enter email addresses (separated by commas)',
        toHelperText: 'Your line manager is added automatically',
        ccHelperText: 'Enter email addresses (separated by commas)',
        sendCopyToYourselfLabel:
          'Send a copy of your learning recommendations to yourself ([YOUR_EMAIL])',
      },
      emailContent: {
        section1: {
          label: 'Email content',
          readOnlyNote: 'This can not be edited',
        },
        section2: {
          label: "Email content cont'd",
          readOnlyNote: 'This can not be edited',
        },
        start: [
          'Hello,',
          "As my line manager, I wanted to share with you the training resources that have been identified by the University's Digital Skills Self-Assessment Quiz as being useful for me, as part of my digital upskilling.",
          'It is recommended that I share these in advance of our Performance Development Review, so that it helps support our conversation.',
          'Here are the resources that have been recommended to me:',
        ].join('\n\n'),
        end: [
          'For more information please visit the Digital Skills SharePoint site:',
          'https://universityofexeteruk.sharepoint.com/sites/DigitalSkillsInitiative',
          '',
          'Regards,',
          '[YOUR_NAME]',
        ].join('\n'),
      },
      learningRecommendations: {
        label: 'Learning recommendations',
        selectNote: 'Select which learning recommendations to share with your manager',
        deselectAll: 'Deselect all',
        selectAll: 'Select all',
      },
      buttons: {
        sendEmail: 'Send email',
        close: 'Close',
      },
      successModal: {
        title: 'Email sent',
        message:
          'Thanks for sharing your learning recommendations with you manager. This will help to support future PDR conversations and inform them of your intended digital upskilling.',
        button: 'Close',
      },
      accessibility: {
        emailRecipientFieldLabel: 'Enter email address',
        emailCcFieldLabel: 'Enter email addresses (separated by commas)',
      },
    },
    cookiesDialog: {
      title: 'Cookies usage',
      description:
        "We use cookies to enhance your browsing experience and analyse site usage. By checking 'Accept' you consent to the use of cookies. For more information on our data usage please view our",
      termsLink: 'Terms of Use',
      checkboxLabel: 'Accept analytics cookies',
      continueButton: 'Continue to Digital Skills',
    },
    toast: {
      bookmarking: {
        bookmarkCreatedMessage: 'has been added to your saved resources.',
        bookmarkDeletedMessage: 'has been removed from your saved resources.',
        viewBookmarksBtnText: 'View saved resources',
        undoBookmarkActionBtnText: 'Undo',
        accessibility: {
          viewBookmarksBtnLabel: 'View your saved learning resources',
          undoBookmarkActionBtnLabel: 'Undo bookmarking action',
        },
      },
    },
    deleteAllBookmarksConfirmationModal: {
      title: 'Remove all saved resources',
      body: 'Removing all saved resources is not reversible. Would you like to continue?',
      confirmButton: 'Yes, remove all my saved resources',
      cancelButton: 'Cancel',
      accessibility: {
        confirmBtnAriaDescription: 'Confirm removing all my saved resources',
        cancelBtnAriaDescription: 'Cancel and go back without deleting my saved resources',
        dialogAriaLabel: 'Confirm that you want to delete all saved resources',
      },
    },
    roleDepartmentMatch: {
      icon: '/assets/target.svg',
      alt: 'Resource is relevant to your role and department',
      description: 'These resources are relevant to your role and department',
      openModalButton: 'What does this mean?',
      modal: {
        title: 'Role specific resources',
        body: 'Your recommended resources have been chosen based on your most recent quiz answers. Some of these resources may also be directly related to digital skills helpful for your role and department. These are labelled with a target icon and are prioritised within each skill category, appearing at the top of their respective category groups.',
        dismissButton: 'Got it',
      },
    },
    feedbackBlock: {
      footer: {
        title: 'Want to explore more resources?',
        cta: 'Search the full digital skills library',
        leaveFeedback: 'Would you like to give us feedback?',
        leaveFeedbackButton: 'Leave feedback',
      },
      feedbackForm: 'https://forms.office.com/e/ZAFsgfiAaj',
    },
  },
  ctas: {
    continue: 'Continue',
    back: 'Back',
    return: 'Return to the home page',
  },
} as const;
